/**
 * ★ 储蓄目标内核 —— **进度派生 / 里程碑 / 节奏 / 腿结构**的唯一实现（I 域 · `DR-050`）
 *
 * 依据：`I域PRD` **§5.1.2**（bucket = 虚拟子罐）· **§5.1.3**（进度与节奏）· **§5.2.1**（攒钱 = 2 腿不计收支）·
 * **§5.3.1**（里程碑每 25% 只升不降）· `AC-I1.6`/`I1.7`/`I1.8` · `AC-I2.1`/`I2.2`/`I2.10` ·
 * `AC-I3.1`/`I3.2` · `AC-I4.3` · `DR-022` v1.1（**超额不截断**）· `DR-023`（端侧金额聚合 = 0）
 *
 * ## 🔴 本文件"拥有"什么（铁律 24：一个机制只有一个实现）
 *
 * | 判定 | 落在哪 |
 * |---|---|
 * | 「已攒」怎么算 | {@link goalProgressOf} —— 入参**必须**是腿聚合的结果，本函数不碰 I/O |
 * | 进度百分比 / 还差多少 / 超额 | 同上（`ratioE4` 是定点白名单里的除法，不在这里重写） |
 * | 里程碑档位 / 只升不降 | {@link milestoneTierOf} · {@link mergeMilestone} |
 * | 节奏（能否如期）与文案 | {@link goalPaceOf} |
 * | 攒/取的两条腿 | {@link goalLegsOf} |
 * | 目标字段的形态校验 | {@link checkGoalShape} |
 *
 * ## 🔴 本文件"不拥有"什么
 *
 * - **不查库**：入参全是已取好的数（`legs` 聚合 / 目标行 / 今天）。这样才能零 I/O 地证伪。
 * - **不推送**：目标提醒由 H 域生成（`AC-I4.2`），I 域只提供 `listReminderCandidates` 这个**只读面**。
 * - **不产生交易类型**：攒钱只是"两条普通的 legs"（`AC-I2.10`）。
 */

import { GoalMove, GOAL_MOVE_LABEL } from '../enums/plan.js';
import { isRealDayString, parseDay, addMonths, daysBetween, type Ymd } from './calendar.js';
import { GoalPolicy } from '../constants/index.js';
import { ceilDiv, centsToYuanText, floorDiv, ratioE4, toCents } from './money.js';
import type { Cents, RatioE4 } from '../types/brand.js';

// ───────────────────────────── ① 进度 ─────────────────────────────

export interface GoalProgressInput {
  /** 目标额（整数分，> 0） */
  targetCents: number;
  /**
   * **已攒** = 落在该目标宿主账户上的腿按方向的净额
   * （`in` 记 +、`out` 记 −）。🔴 由调用方用腿聚合得出，**不是**表里的列。
   */
  savedCents: number;
}

export interface GoalProgress {
  /** 进度（万分之一整数）：`86.0%` ⇒ `8600`。🔴 **不截断**，超额可 > 10000 */
  progressE4: RatioE4;
  /** 还差多少（分）：可**为负**（超额时），便于一处判 `<= 0` */
  remainingCents: Cents;
  /** 已达成（`已攒 ≥ 目标额`） */
  reached: boolean;
  /** 超出部分（分，≥ 0）—— 超额时才有值 */
  overCents: Cents;
}

/**
 * 进度 = `已攒 / 目标额`，**超额不截断**（`AC-I1.6` · `DR-022` v1.1）。
 *
 * 🔴 **不在这里除**：`ratioE4` 是定点白名单里的除法出口（`AC-P1.2`），
 * 本函数只是把「三个派生量」一次性算齐 —— 端侧拿到的就是最终值（`DR-023`）。
 */
export function goalProgressOf(input: GoalProgressInput): GoalProgress {
  const target = toCents(input.targetCents);
  const saved = toCents(input.savedCents);
  const remaining = (target as number) - (saved as number);
  // 目标额 0 的情况由 `checkGoalShape()` 拦在建目标之前；这里退化为 0% 而非无穷大
  const progressE4 = ratioE4(saved, target);
  return {
    progressE4,
    remainingCents: toCents(remaining),
    reached: remaining <= 0,
    overCents: toCents(remaining < 0 ? -remaining : 0),
  };
}

// ───────────────────────────── ② 里程碑 ─────────────────────────────

/** 里程碑档位（`0` = 一个都没点亮） */
export type GoalMilestoneTier = 0 | 25 | 50 | 75 | 100;

/**
 * 当前**算得出**的档位（`AC-I3.1`：每 25% 一次）。
 *
 * 🔴 **用比较、不用除法**：档位集合只有 5 个值，`if` 链既无浮点风险，也不需要
 * `Math.floor`（那在 `money.ts` 之外被禁）。→ 25% 档位在 `2500 ≤ e4 < 5000` 成立。
 * 🔴 **超额停在 100**：130% 的"当前档位"仍是 100 —— 超出多少由 `overCents` 表达，不靠档位。
 */
export function milestoneTierOf(progressE4: RatioE4): GoalMilestoneTier {
  const e4 = progressE4 as number;
  if (e4 >= 10000) return 100;
  if (e4 >= 7500) return 75;
  if (e4 >= 5000) return 50;
  if (e4 >= 2500) return 25;
  return 0;
}

/**
 * 把库里的 `milestone_reached`（`text`）解析成档位。
 *
 * 🔴 **容错到"当 0 处理"而不是抛错**：这一列在 v1 迁移过的库里可能为空 / 有 `'25,50'`
 * 之类的手写值。读到不认识的值就当「一个都没点亮」—— 它**只会少点亮一次**，
 * 下次进度越过该档位就自动补上（`mergeMilestone` 取 max）。抛错则会让整个目标列表 500。
 */
export function parseMilestoneTier(raw: string | null | undefined): GoalMilestoneTier {
  if (raw === null || raw === undefined) return 0;
  const n = Number(raw.trim());
  if (n === 25 || n === 50 || n === 75 || n === 100) return n;
  return 0;
}

/**
 * 🔴 **只升不降**（`AC-I3.2` / E11）：取历史最大值，**不是**覆盖。
 *
 * 取出导致进度回落到 40% 时，本次算出的档位是 25 —— 但库里已是 50 ⇒ 结果仍是 50。
 * 「覆盖」在这里就是"熄灭"，而 PRD 明确要求点亮过的档位**不熄灭**。
 */
export function mergeMilestone(
  persisted: GoalMilestoneTier,
  computed: GoalMilestoneTier,
): GoalMilestoneTier {
  return persisted >= computed ? persisted : computed;
}

/** 里程碑轨道的一格（端侧只渲染，**不自己算 `floor(pct/25)`**） */
export interface GoalMilestoneSlot {
  tier: GoalMilestoneTier;
  /** 该档位是否已点亮（`tier <= highestReached`） */
  reached: boolean;
}

/** 轨道 = 全档位 + 每格是否点亮（由**落库的最高档位**决定，不由当前进度决定） */
export function milestoneTrackOf(highest: GoalMilestoneTier): GoalMilestoneSlot[] {
  return GoalPolicy.MilestoneTiers.map((tier) => ({ tier, reached: tier <= highest }));
}

// ───────────────────────────── ③ 节奏 ─────────────────────────────

/**
 * 节奏判定结果（`AC-I1.7`：**无截止日 ⇒ `null`**）。
 *
 * | 值 | 含义 |
 * |---|---|
 * | `null` | 无截止日 ⇒ **不判节奏**（不是"正常"，是"不适用"） |
 * | `tooEarly` | 观察不足 {@link GoalPolicy.MinMonthsForPace} 个月 ⇒ 不猜 |
 * | `reached` | 已达成 |
 * | `overdue` | 截止日已过且未达成（E10：**不判失败**，只提示调整） |
 * | `onTrack` | 按当前节奏能在截止日前达成 |
 * | `behind` | 按当前节奏会晚于截止日 |
 */
export type GoalPace = 'reached' | 'onTrack' | 'behind' | 'overdue' | 'tooEarly';

export interface GoalPaceInput {
  targetCents: number;
  savedCents: number;
  /** 截止日（`YYYY-MM-DD`）—— `null`/`undefined` ⇒ 不判 */
  dueDate: string | null | undefined;
  /** 今天（账本本地日历日） */
  today: string;
  /**
   * **第一笔攒钱的日期**（该目标最早的非软删流水日）。
   * `null` ⇒ 还没攒过 ⇒ 算不出月均速率 ⇒ `tooEarly`。
   */
  firstMoveDate: string | null | undefined;
}

export interface GoalPaceResult {
  /** `null` = 无截止日（**不判**） */
  pace: GoalPace | null;
  /** 观察到的整月数（不足 1 ⇒ `null`，不编造） */
  monthsElapsed: number | null;
  /** 月均攒速（分/月，向下取整）—— 未形成速率时 `null` */
  monthlyRateCents: Cents | null;
  /** 预计达成日；算不出 `null` */
  projectedDate: string | null;
  /** 距截止日的天数（负数 = 已过期）；无截止日 `null` */
  daysLeft: number | null;
  /**
   * 面向用户的一句话（`AC-I4.3`：**建议句式**，不做羞耻式）。
   * 无截止日 ⇒ `null`（前端就显示"还差 ¥X"，不说节奏）。
   */
  hint: string | null;
}

/** 两个日历日之间的**整月数**（不足一个月按 0 计；不回退到浮点） */
function wholeMonthsBetween(from: string, to: string): number {
  const a: Ymd = parseDay(from);
  const b: Ymd = parseDay(to);
  let months = (b.y - a.y) * 12 + (b.m - a.m);
  // 日不足 ⇒ 还差几天才算整月
  if (b.d < a.d) months -= 1;
  return months < 0 ? 0 : months;
}

/**
 * 节奏（`§5.1.3`）。**只有"有截止日 + 攒过至少一个月"才给结论**，其余一律诚实留白。
 *
 * 🔴 **为什么月均速率要向下取整**：向上取整会让预计达成日提前，用户照此排预算就会差一截。
 * 缺口那边反过来用 `ceilDiv`（差 1 分也要多攒一个月）—— **两头都朝"保守"偏**。
 */
export function goalPaceOf(input: GoalPaceInput): GoalPaceResult {
  const due = input.dueDate ?? null;
  if (due === null || due === '') {
    // 🔴 无截止日：**不判节奏**（`AC-I1.7`）—— 连"预计达成日"都不给，不是给个默认值
    return {
      pace: null,
      monthsElapsed: null,
      monthlyRateCents: null,
      projectedDate: null,
      daysLeft: null,
      hint: null,
    };
  }

  const target = toCents(input.targetCents);
  const saved = toCents(input.savedCents);
  const remaining = (target as number) - (saved as number);
  const daysLeft = daysBetween(input.today, due);

  // ① 已达成 —— 与「有没有速率」无关，直接给结论
  if (remaining <= 0) {
    return {
      pace: 'reached',
      monthsElapsed: null,
      monthlyRateCents: null,
      projectedDate: input.today,
      daysLeft,
      hint: `已达成，超出 ${fmtYuan(remaining < 0 ? -remaining : 0)}`,
    };
  }

  // ② 已过期且未达成 —— E10：**不判失败**，只提示
  if (daysLeft < 0) {
    return {
      pace: 'overdue',
      monthsElapsed: null,
      monthlyRateCents: null,
      projectedDate: null,
      daysLeft,
      hint: `已过截止日，还差 ${fmtYuan(remaining)}；可以调整截止日或目标金额`,
    };
  }

  // ③ 还没攒过 / 观察不足 —— 不猜（`MinMonthsForPace`）
  const first = input.firstMoveDate ?? null;
  if (first === null || first === '') {
    return {
      pace: 'tooEarly',
      monthsElapsed: null,
      monthlyRateCents: null,
      projectedDate: null,
      daysLeft,
      hint: `还没开始攒；想按时完成，可以先攒第一笔`,
    };
  }
  const months = wholeMonthsBetween(first, input.today);
  if (months < GoalPolicy.MinMonthsForPace) {
    return {
      pace: 'tooEarly',
      monthsElapsed: months,
      monthlyRateCents: null,
      projectedDate: null,
      daysLeft,
      hint: `刚开始攒，还看不出节奏；还差 ${fmtYuan(remaining)}`,
    };
  }

  // ④ 有速率：算预计达成日
  const rate = floorDiv(saved as number, months);
  if (rate <= 0) {
    // 攒过又全取出 ⇒ 净额为 0 或负：没有正向速率，只能给"还没有稳定节奏"
    return {
      pace: 'behind',
      monthsElapsed: months,
      monthlyRateCents: toCents(0),
      projectedDate: null,
      daysLeft,
      hint: `目前还没有形成稳定的攒钱节奏；还差 ${fmtYuan(remaining)}，想按时完成可设定每期金额`,
    };
  }

  const monthsNeeded = ceilDiv(remaining, rate);
  const projected = addMonths(input.today, monthsNeeded);
  const onTrack = projected <= due;
  return {
    pace: onTrack ? 'onTrack' : 'behind',
    monthsElapsed: months,
    monthlyRateCents: toCents(rate),
    projectedDate: projected,
    daysLeft,
    hint: onTrack
      ? `按当前节奏，约 ${projected} 达成`
      : `按当前节奏，约 ${projected} 达成；想按时完成可上调每期金额`,
  };
}

/**
 * 分（整数）→ 「¥1234.56」短文本（**无千分位**）。
 *
 * 🔴 **复用 `centsToYuanText()`**，不在这里再做一次"分转元"：
 * 那是一次除法，而 `/` 与 `Math.floor` 在 `money.ts` 之外被 ESLint 全封（`AC-P1.2`）。
 * 本函数的存在只是给它加上币种符号而已。
 *
 * ⚠️ 只用于**内核生成的提示句**。端侧渲染金额必须走 `formatMoneyText()`（带币种 + 千分位）。
 */
function fmtYuan(cents: number): string {
  return `¥${centsToYuanText(toCents(cents))}`;
}

// ───────────────────────────── ④ 腿结构 ─────────────────────────────

/** 攒/取的一条腿（结构等价于 `ShapeLeg`，但**不含分类** —— 内部互转不挂分类） */
export interface GoalLeg {
  accountId: string;
  direction: 'in' | 'out';
  amount: number;
  categoryId: null;
  excludeFromStats: true;
}

export interface GoalLegsInput {
  move: GoalMove;
  amountCents: number;
  /** 目标**宿主账户**（`savings_goals.account_id`；首次攒钱时由请求补上） */
  hostAccountId: string;
  /** 对手账户：攒 = 来源；取 = 去向 */
  counterAccountId: string;
}

/**
 * 攒/取的两条腿（`AC-I2.1`/`I2.2`/`I2.10`）—— **恒 2 条，顺序固定**。
 *
 * | 动作 | 第 1 条 | 第 2 条 |
 * |---|---|---|
 * | `save` | 宿主 `in`（钱进目标账户） | 来源 `out` |
 * | `withdraw` | 宿主 `out`（钱出目标账户） | 去向 `in` |
 *
 * 🔴 **两条腿都 `excludeFromStats = true`、`categoryId = null`** —— 与 A 域转账同一机制。
 * 少标一条，这笔攒钱就会以"支出"或"收入"的形态进报表（`AC-I2.3` 红线）。
 *
 * ⚠️ **顺序为什么固定**：L3 / 贡献流的展示要能一眼认出"哪条是目标腿"。
 * 靠"第一条"这个约定比再存一个字段便宜，但**必须写死在这里**（不散落在调用点）。
 */
export function goalLegsOf(input: GoalLegsInput): GoalLeg[] {
  const amount = input.amountCents;
  const hostLeg: GoalLeg = {
    accountId: input.hostAccountId,
    direction: input.move === GoalMove.Save ? 'in' : 'out',
    amount,
    categoryId: null,
    excludeFromStats: true,
  };
  const counterLeg: GoalLeg = {
    accountId: input.counterAccountId,
    direction: input.move === GoalMove.Save ? 'out' : 'in',
    amount,
    categoryId: null,
    excludeFromStats: true,
  };
  return [hostLeg, counterLeg];
}

/** 攒/取流水的默认备注（用户没填时用）—— 唯一文案源 */
export function goalMoveNote(move: GoalMove, goalName: string): string {
  return `${GOAL_MOVE_LABEL[move]} · ${goalName}`;
}

/**
 * **真实存在的日历日**（`2026-13-01` / `2026-02-30` 都不算）。
 *
 * 🔴 为什么不能只用 `isDayString()`：它自己的注释写着「**不校验日期是否真实存在**，只校验形态」。
 * 形态对但值非法的日期会一路走到 `addMonths()` —— 那里 `Date.UTC(2026, 12, 1)` **静默归一**成
 * 2027-01-01，于是"截止日 2026-13-01"会变成一个看起来完全正常的日期。
 * ⇒ 判"能不能存"必须比"长得像不像"严一档。
 */
function isRealDay(day: string): boolean {
  // 🔴 实现已收拢到 `calendar.ts`（批 99 B2：投资域的到期日也要同一判据）
  return isRealDayString(day);
}

// ───────────────────────────── ⑤ 形态校验 ─────────────────────────────

export type GoalField = 'name' | 'target' | 'dueDate' | 'account';

export interface GoalIssue {
  field: GoalField;
  message: string;
}

export interface GoalShapeInput {
  name: string;
  targetCents: number;
  dueDate: string | null | undefined;
  /** 关联账户（可空 —— 空 ⇒ 首次攒钱时再绑定，`DR-050` D3） */
  accountId: string | null | undefined;
}

/** 目标字段的形态校验（**服务端与端侧共用** —— 端侧用它做即时提示，服务端用它做最终裁决） */
export function checkGoalShape(input: GoalShapeInput): GoalIssue[] {  const issues: GoalIssue[] = [];
  const name = input.name.trim();
  if (name === '') {
    issues.push({ field: 'name', message: '请填写目标名称' });
  } else if (name.length > GoalPolicy.NameMaxLength) {
    issues.push({ field: 'name', message: `目标名称不超过 ${String(GoalPolicy.NameMaxLength)} 个字` });
  }
  if (!Number.isInteger(input.targetCents)) {
    issues.push({ field: 'target', message: '目标金额必须是整数分' });
  } else if (input.targetCents <= 0) {
    issues.push({ field: 'target', message: '目标金额必须大于 0' });
  }
  const due = input.dueDate ?? null;
  if (due !== null && due !== '' && !isRealDay(due)) {
    issues.push({ field: 'dueDate', message: '截止日格式应为 YYYY-MM-DD（也可以留空）' });
  }
  const acc = input.accountId ?? null;
  if (acc !== null && acc.trim() === '') {
    issues.push({ field: 'account', message: '关联账户不能是空字符串（不想关联请留空）' });
  }
  return issues;
}
